import * as THREE from "three";
import gsap from "gsap";
import { SQUARE_COUNT, TILE_SIZE, squareToWorld } from "./boardCoordinates";
import {
  tileFamilyForSquare,
  familyRestColor,
  familyEndColor,
  COMPLETION_PULSE_COLOR,
  type TileFamily,
} from "../animation/BoardColorAnimation";
import type { BoardTheme, ThemeTransition } from "../app/Settings";

const TILE_HEIGHT = 0.12;
const TILE_GAP = 0.02;

interface TileState {
  readonly family: TileFamily;
  visited: boolean;
}

interface ThemeSpec { light: number; dark: number; visitedLight: number; visitedDark: number; texture?: "wood" | "marble" | "stone"; }
type ConcreteTheme = Exclude<BoardTheme, "new-cycle" | "classic-cycle" | "all-cycle">;
const THEMES: Record<Exclude<ConcreteTheme, "original-color">, ThemeSpec> = {
  "ivory-forest": { light: 0xd8cba9, dark: 0x244b43, visitedLight: 0x8da9a1, visitedDark: 0x162d2b },
  walnut: { light: 0x6b4b38, dark: 0x2b1c16, visitedLight: 0x9a7456, visitedDark: 0x3b2920, texture: "wood" },
  espresso: { light: 0x49372d, dark: 0x201916, visitedLight: 0x725746, visitedDark: 0x332721, texture: "wood" },
  navy: { light: 0x294055, dark: 0x111c27, visitedLight: 0x456b83, visitedDark: 0x1d3444 },
  forest: { light: 0x315347, dark: 0x142820, visitedLight: 0x527765, visitedDark: 0x244337 },
  slate: { light: 0x46535c, dark: 0x20282d, visitedLight: 0x718089, visitedDark: 0x354149, texture: "stone" },
  burgundy: { light: 0x59313a, dark: 0x28161b, visitedLight: 0x82505a, visitedDark: 0x45262e },
  indigo: { light: 0x47405f, dark: 0x201d2e, visitedLight: 0x716887, visitedDark: 0x39344d },
  parchment: { light: 0xc7b896, dark: 0x665842, visitedLight: 0xe0d1ac, visitedDark: 0x88785c },
  marble: { light: 0xd7d2c6, dark: 0x343a3d, visitedLight: 0xf0e8d5, visitedDark: 0x566064, texture: "marble" },
};
const NEW_THEME_CYCLE: ConcreteTheme[] = ["forest", "slate", "burgundy", "indigo", "navy", "walnut", "espresso", "marble", "parchment", "ivory-forest"];
const ALL_THEME_CYCLE: ConcreteTheme[] = ["original-color", ...NEW_THEME_CYCLE];

/**
 * The 8x8 board. One shared `BoxGeometry` reused for all 64 tiles; each
 * tile keeps its own `MeshStandardMaterial`, created once at construction
 * and only ever mutated afterward -- never recreated per move or per tour.
 *
 * Color model: a tile's checkerboard family -- yellow or green, from
 * `(row+col) % 2`, see `BoardColorAnimation.getTileFamily` -- is fixed
 * forever at construction and never changes. Only a tile the knight has
 * actually landed on ever changes color, and it changes exactly once
 * *within a tour*: it snaps directly from its family's rest color to its
 * family's visited color (`applyPermanentVisitColor`) -- no intermediate
 * shade, and the result does not depend on when in the tour the visit
 * happens. Since a knight move always flips square parity, consecutive
 * landings always alternate family, never a gradual walk through
 * intermediate hues.
 *
 * *Across* tours, the two colors themselves advance: `tourIndex` selects a
 * pair from `BoardColorAnimation`'s designed, cyclic palette, and a tour's
 * visited pair is exactly the next tour's rest pair (see `resetBoardColors`
 * -- the caller is expected to pass an incrementing tour index) -- so
 * consecutive tours chain smoothly into a fresh, "interesting" pair rather
 * than jumping back to the same two colors every time. Nothing updates on
 * a whole-board timer and nothing accumulates within a tour -- this is
 * what keeps the checkerboard identity readable throughout, instead of
 * decaying into a rainbow or a wash of brightness.
 */
export class Board3D {
  readonly group: THREE.Group;
  private materials: THREE.MeshStandardMaterial[] = [];
  private geometry: THREE.BoxGeometry;
  private tiles: TileState[] = [];
  private tourIndex = 0;
  private theme: BoardTheme = "original-color";
  private resolvedTheme: ConcreteTheme = "original-color";
  private morphFrom: ConcreteTheme = "forest";
  private morphTo: ConcreteTheme = "slate";
  private transitionMode: ThemeTransition = "between-tours";
  private texture: THREE.CanvasTexture | null = null;

  constructor(scene: THREE.Scene) {
    this.group = new THREE.Group();
    this.group.name = "Board3D";
    this.geometry = new THREE.BoxGeometry(TILE_SIZE - TILE_GAP, TILE_HEIGHT, TILE_SIZE - TILE_GAP);

    for (let square = 0; square < SQUARE_COUNT; square++) {
      const family = tileFamilyForSquare(square);
      this.tiles.push({ family, visited: false });

      const material = new THREE.MeshStandardMaterial({
        color: familyRestColor(family, this.tourIndex),
        roughness: 0.55,
        metalness: 0.05,
        emissive: new THREE.Color(0x000000),
        emissiveIntensity: 0,
      });
      const tile = new THREE.Mesh(this.geometry, material);
      tile.position.copy(squareToWorld(square, -TILE_HEIGHT / 2));
      tile.receiveShadow = true;
      tile.userData.square = square;
      this.materials.push(material);
      this.group.add(tile);
    }

    scene.add(this.group);
  }

  /**
   * Starts a new tour: every tile -- visited or not, from whatever tour
   * just finished -- returns to its family's rest color for this tour's
   * position in the palette (`tourIndex`, expected to increment by 1 each
   * time the caller starts a new tour). That rest color is exactly the
   * previous tour's visited color, so the palette advances smoothly across
   * tours instead of resetting. No brightness, emissive intensity, or
   * color *state* carries over -- only the palette position does.
   */
  resetBoardColors(tourIndex: number): void {
    this.tourIndex = tourIndex;
    if (this.theme === "new-cycle" && this.transitionMode === "per-move") {
      this.morphFrom = NEW_THEME_CYCLE[tourIndex % NEW_THEME_CYCLE.length];
      this.morphTo = NEW_THEME_CYCLE[(tourIndex + 1) % NEW_THEME_CYCLE.length];
      this.applyResolvedTheme(this.morphFrom);
    } else if (this.theme === "new-cycle") this.applyResolvedTheme(NEW_THEME_CYCLE[tourIndex % NEW_THEME_CYCLE.length]);
    else if (this.theme === "all-cycle") this.applyResolvedTheme(ALL_THEME_CYCLE[tourIndex % ALL_THEME_CYCLE.length]);
    else if (this.theme === "classic-cycle") this.applyResolvedTheme("original-color");
    for (let square = 0; square < SQUARE_COUNT; square++) {
      const tileState = this.tiles[square];
      tileState.visited = false;

      const material = this.materials[square];
      gsap.killTweensOf(material.color);
      const target = this.tileColor(tileState, false);
      gsap.to(material.color, { r: target.r, g: target.g, b: target.b, duration: .85, ease: "power2.inOut" });
      material.emissive.set(0x000000);
      material.emissiveIntensity = 0;
    }
  }

  /**
   * Assigns the permanent color for the square the knight just landed on
   * (or, for the tour's starting square, the color assigned before the
   * first jump): an immediate, binary snap to that tile's family visited
   * color for the current tour (`familyEndColor`) -- never an intermediate
   * shade, and never a function of visit order. Called exactly once per
   * square per tour; the resulting color is then left untouched until the
   * next `resetBoardColors()` -- later moves never revisit or re-tint it.
   */
  applyPermanentVisitColor(square: number): void {
    const tileState = this.tiles[square];
    tileState.visited = true;
    this.materials[square].color.copy(this.tileColor(tileState, true));
  }

  setTourProgress(_visitedCount: number): void {
    if (this.theme !== "new-cycle" || this.transitionMode !== "per-move") return;
    for (let square = 0; square < SQUARE_COUNT; square++) {
      const family = this.tiles[square].family;
      // Spatial morph: only squares already landed on adopt the next
      // board theme. Unvisited squares retain the source theme, making
      // the tour itself visibly "paint" Forest into Slate (then Slate
      // into Burgundy, etc.) one landing at a time.
      const theme = this.tiles[square].visited ? this.morphTo : this.morphFrom;
      this.materials[square].color.copy(this.themeColor(theme, family, false));
    }
  }

  /**
   * Snaps every tile to its family's visited color for the current tour
   * once the tour is complete. Since `applyPermanentVisitColor` already
   * assigns this exact color on landing, this is normally a no-op for the
   * 64 visited squares; it exists as an explicit guarantee that the
   * completed board is always exactly two endpoint colors (covers e.g. an
   * open tour that hasn't visited all 64 squares by design, or any future
   * path that skips a landing).
   */
  normalizeCompletedBoard(): void {
    if (this.theme === "new-cycle" && this.transitionMode === "per-move") { this.setTourProgress(64); return; }
    for (let square = 0; square < SQUARE_COUNT; square++) {
      const tileState = this.tiles[square];
      this.materials[square].color.copy(this.tileColor(tileState, true));
    }
  }

  /** Read-only accessor for tests/diagnostics: the tile's current permanent base color. */
  getTileColor(square: number): THREE.Color {
    return this.materials[square].color.clone();
  }

  /** Read-only accessor for tests/diagnostics: the tile's current emissive intensity (temporary glow only). */
  getTileEmissiveIntensity(square: number): number {
    return this.materials[square].emissiveIntensity;
  }

  /** Whole-board glow used during COMPLETING_TOUR; `intensity` is 0..~1. Emissive only. */
  setCompletionPulse(intensity: number): void {
    for (const material of this.materials) {
      gsap.killTweensOf(material);
      material.emissive.copy(COMPLETION_PULSE_COLOR);
      material.emissiveIntensity = intensity;
    }
  }

  clearPulse(): void {
    for (const material of this.materials) {
      material.emissiveIntensity = 0;
    }
  }

  setTheme(theme: BoardTheme): void {
    if (theme === this.theme && this.materials[0]?.map === this.texture) return;
    this.theme = theme;
    const resolved = theme === "new-cycle" ? NEW_THEME_CYCLE[this.tourIndex % NEW_THEME_CYCLE.length]
      : theme === "all-cycle" ? ALL_THEME_CYCLE[this.tourIndex % ALL_THEME_CYCLE.length]
      : theme === "classic-cycle" ? "original-color" : theme;
    this.applyResolvedTheme(resolved);
  }

  setTransitionMode(mode: ThemeTransition): void { this.transitionMode = mode; }

  private applyResolvedTheme(theme: ConcreteTheme): void {
    this.resolvedTheme = theme;
    this.texture?.dispose();
    const textureKind = theme === "original-color" ? undefined : THEMES[theme].texture;
    this.texture = textureKind ? this.makeTexture(textureKind) : null;
    for (let square = 0; square < SQUARE_COUNT; square++) {
      const material = this.materials[square];
      material.map = this.texture;
      material.roughness = textureKind === "marble" ? .34 : textureKind === "wood" ? .62 : .55;
      const target = this.tileColor(this.tiles[square], this.tiles[square].visited);
      gsap.killTweensOf(material.color);
      gsap.to(material.color, { r: target.r, g: target.g, b: target.b, duration: .55, ease: "power2.out" });
      material.needsUpdate = true;
    }
  }

  private tileColor(tile: TileState, visited: boolean): THREE.Color {
    if (this.resolvedTheme === "original-color") return visited ? familyEndColor(tile.family, this.tourIndex) : familyRestColor(tile.family, this.tourIndex);
    const spec = THEMES[this.resolvedTheme];
    const value = tile.family === "yellow" ? (visited ? spec.visitedLight : spec.light) : (visited ? spec.visitedDark : spec.dark);
    return new THREE.Color(value);
  }

  private themeColor(theme: ConcreteTheme, family: TileFamily, visited: boolean): THREE.Color {
    if (theme === "original-color") return visited ? familyEndColor(family, this.tourIndex) : familyRestColor(family, this.tourIndex);
    const spec = THEMES[theme];
    return new THREE.Color(family === "yellow" ? (visited ? spec.visitedLight : spec.light) : (visited ? spec.visitedDark : spec.dark));
  }

  private makeTexture(kind: "wood" | "marble" | "stone"): THREE.CanvasTexture {
    const canvas = document.createElement("canvas"); canvas.width = 128; canvas.height = 128;
    const c = canvas.getContext("2d")!; c.fillStyle = "#b8b8b8"; c.fillRect(0, 0, 128, 128);
    let seed = kind === "wood" ? 41 : kind === "marble" ? 73 : 97;
    const random = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
    c.globalCompositeOperation = "multiply";
    if (kind === "wood") {
      for (let y = 3; y < 128; y += 7) { c.strokeStyle = `rgba(74,42,24,${.05 + random() * .08})`; c.lineWidth = 1 + random() * 2; c.beginPath(); c.moveTo(0,y); for(let x=0;x<=128;x+=12)c.lineTo(x,y+Math.sin(x*.09+random())*3); c.stroke(); }
    } else if (kind === "marble") {
      for (let i=0;i<12;i++) { c.strokeStyle = `rgba(65,72,76,${.035 + random()*.07})`; c.lineWidth=.5+random()*1.5; c.beginPath(); c.moveTo(-10,random()*128); c.bezierCurveTo(35,random()*128,80,random()*128,138,random()*128); c.stroke(); }
    } else {
      for (let i=0;i<700;i++) { const g=Math.floor(90+random()*90); c.fillStyle=`rgba(${g},${g},${g},.08)`; c.fillRect(random()*128,random()*128,1,1); }
    }
    const texture = new THREE.CanvasTexture(canvas); texture.wrapS = texture.wrapT = THREE.RepeatWrapping; texture.repeat.set(1.4,1.4); return texture;
  }

  dispose(): void {
    for (const material of this.materials) {
      gsap.killTweensOf(material);
      material.dispose();
    }
    this.geometry.dispose();
    this.texture?.dispose();
    this.materials = [];
    this.tiles = [];
  }
}
